import {
  Button,
  DropdownMenuItem,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@ais-ui/ui-core';
import React, { SetStateAction, useState } from 'react';
import { buffer } from '@turf/buffer';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { useLocalization } from '@ais-ui/locale-management';
import { useLengthUnits } from '../../Units/useLengthUnits';
import { Units } from '@ais-ui/utils';

interface CreateBufferDropdownButtonProps {
  shape: GeoJson;
  setOpen: React.Dispatch<SetStateAction<boolean>>;
  setOpenPopoverBuffer: React.Dispatch<SetStateAction<boolean>>;
  setShowCreateDialog: React.Dispatch<SetStateAction<boolean>>;
  setBufferShape: React.Dispatch<SetStateAction<GeoJson | undefined>>;
  openPopoverBuffer: boolean;
}

export const CreateBufferDropdownButton = ({
  shape,
  setOpen,
  setOpenPopoverBuffer,
  setShowCreateDialog,
  setBufferShape,
  openPopoverBuffer,
}: CreateBufferDropdownButtonProps) => {
  const { messages } = useLocalization();
  const { selectedUnit } = useLengthUnits();
  const [bufferValue, setBufferValue] = useState('');

  // Get the appropriate input unit and conversion factor to meters
  const getInputUnitInfo = () => {
    switch (selectedUnit) {
      case Units.METRIC:
      case Units.MARITIME:
        return { unit: 'meters', abbreviation: 'm', toMeters: 1 };
      case Units.IMPERIAL:
        return { unit: 'feet', abbreviation: 'ft', toMeters: 0.3048 };
      default:
        return { unit: 'meters', abbreviation: 'm', toMeters: 1 };
    }
  };

  const { unit, abbreviation, toMeters } = getInputUnitInfo();

  const onCreateBuffer = (bufferInUserUnits: number) => {
    // Convert user input to meters for the buffer calculation
    const bufferInMeters = bufferInUserUnits * toMeters;

    const buffered = buffer(
      shape as any,
      bufferInMeters / 1000, // turf.buffer expects kilometers
    ) as GeoJson;

    const shapeName = shape.properties.name ?? shape.properties.id ?? 'shape';
    buffered.properties.description = `${bufferInUserUnits} ${abbreviation} buffer around ${shapeName}`;
    buffered.properties.name = `${shapeName} buffer`;

    setShowCreateDialog(true);
    setBufferShape(buffered);
    setOpenPopoverBuffer(false);
    setOpen(false);
  };

  // Set appropriate max values based on unit
  const getMaxValue = () => {
    switch (selectedUnit) {
      case Units.IMPERIAL:
        return 328084; // ~100km in feet
      case Units.METRIC:
      case Units.MARITIME:
      default:
        return 100000; // 100km in meters
    }
  };

  const maxValue = getMaxValue();

  return (
    <Popover open={openPopoverBuffer}>
      <PopoverTrigger asChild>
        <DropdownMenuItem
          onClick={(event) => {
            event.preventDefault();
            setOpenPopoverBuffer(true);
          }}
        >
          {messages.shapeCreateLineBuffer}
        </DropdownMenuItem>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="left"
        className="flex flex-col space-y-4"
      >
        <div className="flex flex-col space-y-2">
          <Label>Buffer distance ({unit})</Label>
          <div className="flex items-center space-x-2">
            <Input
              type="number"
              min={0}
              max={maxValue}
              placeholder={`Enter distance in ${unit}`}
              value={bufferValue}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                if (isNaN(value) || value < 0) {
                  setBufferValue('0');
                } else if (value > maxValue) {
                  setBufferValue(maxValue.toString());
                } else {
                  setBufferValue(value.toString());
                }
              }}
            />
            <span className="text-sm text-muted-foreground min-w-8">
              {abbreviation}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Creates a zone around the line at the specified distance
          </p>
        </div>
        <Button
          onClick={() => onCreateBuffer(parseFloat(bufferValue))}
          disabled={!bufferValue || parseFloat(bufferValue) <= 0}
        >
          Start creation
        </Button>
      </PopoverContent>
    </Popover>
  );
};
